import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { semanticSearchApi } from '@/lib/api';

export const semanticSearchKeys = {
  all: ['semanticSearch'] as const,
  search: (query: string) => [...semanticSearchKeys.all, 'search', query] as const,
  status: () => [...semanticSearchKeys.all, 'status'] as const,
};

/**
 * Hook for semantic search
 */
export function useSemanticSearch(
  query: string,
  options: { threshold?: number; limit?: number; enabled?: boolean } = {}
) {
  const { threshold, limit, enabled = true } = options;

  return useQuery({
    queryKey: semanticSearchKeys.search(query),
    queryFn: () => semanticSearchApi.search(query, { threshold, limit }),
    enabled: enabled && query.trim().length >= 2,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Hook for getting search index status
 */
export function useSearchIndexStatus() {
  return useQuery({
    queryKey: semanticSearchKeys.status(),
    queryFn: () => semanticSearchApi.getStatus(),
    staleTime: 1000 * 30, // 30 seconds
  });
}

/**
 * Hook for migrating bookmarks to have embeddings
 */
export function useMigrateBookmarks() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => semanticSearchApi.migrate(),
    onSuccess: () => {
      // Invalidate status after migration
      queryClient.invalidateQueries({ queryKey: semanticSearchKeys.status() });
    },
  });
}

/**
 * Hook for generating embeddings (for debugging)
 */
export function useGenerateEmbedding() {
  return useMutation({
    mutationFn: (text: string) => semanticSearchApi.generateEmbedding(text),
  });
}
